import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Moment from 'react-moment';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Tooltip } from '@mui/material';
import { Calendar, Coins, Mail, Receipt, ShoppingBag, User } from 'lucide-react';
import { useSelector } from 'react-redux';
import { PaymentStatus } from '../../../../constants';
import { Routes } from '../../../../config/routes';
import { Urls } from '../../../../config/urls';
import l from '../../../../lang';
import { useClients } from '../../../../store/clients';
import { useTransactions } from '../../../../store/transactions';
import { getTask } from '../../../../services';
import { Filters } from '../../../../store/interfaces';
import { Transaction } from '../../../../store/transactions/interfaces';
import { createDate, getMappedCountryCode } from '../../../../utils';
import { countryName } from '../../../../utils/payment-methods-utils';
import { getEciBrandResult, ECI_RESULT, isTransactionRefunded } from '../../../../utils/transactions-utils';
import { segmentTransactionsTransactionDetail } from '../../../../utils/analytics';
import TopDashboardSection from '../../../common/top-dashboard-section';
import ButtonLink from '../../../common/button-link';
import Card from '../../../common/card';
import Table from '../../../common/table';
import Loading from '../../../common/loading';
import ChipStatus from '../../../common/chip-status';
import ButtonMore from '../../../common/button-more';
import { ReactComponent as BackIcon } from '../../../../assets/icons/back.svg';
import { ReactComponent as CopyIcon } from '../../../../assets/icons/copy.svg';
import { ReactComponent as DeclinedWarningIcon } from '../../../../assets/icons/warning-alert.svg';
import { ReactComponent as ChargebackIcon } from '../../../../assets/icons/card.svg';
import TransactionCompletedStatusIcon from '../../../common/transaction-completed-status-icon';
import TransactionStatusIcon from '../../../common/transaction-status-icon';
import { TransactionTooltipButton } from '../../transactions/transaction-tooltip';
import {
  formatTransactedAmount,
  formatTransactedAmountValue,
  getClientFullName,
  getClientInitial,
  getClientStatus,
} from '../utils';
import './styles.scss';

const ClientDetailPage = () => {
  const history = useHistory();
  const { client_id: clientIdParam } = useParams<{ client_id: string }>();
  const { clientDetail, allClients, loading: clientsLoading, getClients, setClientDetail } = useClients();
  const { allTransactions, loading: transactionsLoading, getTransactions } = useTransactions();
  const [copiedInvoiceId, setCopiedInvoiceId] = useState<string | null>(null);
  const [transactionsPage, setTransactionsPage] = useState(0);
  const [copiedEmailInvoiceId, setCopiedEmailInvoiceId] = useState<string | null>(null);
  const { auth } = useSelector((state: any) => state);
  const [clientSummary, setClientSummary] = useState<{ transactedVolume: number; numberOfPurchases: number; averageTicket: number; firstTransactionDate?: string; lastTransactionDate?: string; status?: string } | null>(null);

  const client = useMemo(() => {
    if (clientDetail && String(clientDetail.id) === clientIdParam) {
      return clientDetail;
    }

    return allClients?.data?.find(item => String(item.id) === clientIdParam);
  }, [allClients?.data, clientDetail, clientIdParam]);

  const transactionFilter = useMemo<Filters>(() => {
    if (!clientIdParam) {
      return {};
    }

    return { client_id: clientIdParam };
  }, [clientIdParam]);

  useEffect(() => {
    if (!allClients?.data?.length) {
      getClients();
    }
  }, []);

  useEffect(() => {
    if (!clientsLoading && allClients && !client) {
      history.replace(Routes.CLIENTS);
    }
  }, [allClients, client, clientsLoading, history]);

  useEffect(() => {
    if (!client) {
      return;
    }

    setClientDetail(client);
  }, [client]);

  useEffect(() => {
    if (!clientIdParam || !auth.token) return;

    const fetchSummary = async () => {
      const { response } = await getTask({
        url: Urls.api.clientDetail.replace(':clientId', clientIdParam),
        token: auth.token,
      });
      if (response) {
        const clientData = response.client;
        const summaryData = response.summary;
        setClientSummary({
          ...(summaryData || {}),
          firstTransactionDate: clientData?.firstTransactionDate,
          lastTransactionDate: clientData?.lastTransactionDate,
          status: clientData?.status,
        });
      }
    };

    fetchSummary();
  }, [clientIdParam, auth.token]);

  useEffect(() => {
    if (!clientIdParam) {
      return;
    }

    getTransactions(transactionsPage, transactionFilter);
  }, [clientIdParam, transactionFilter, transactionsPage]);

  useEffect(() => {
    return () => {
      setClientDetail(null);
    };
  }, []);

  const backToClients = () => {
    setClientDetail(null);
    history.push(Routes.CLIENTS);
  };

  const openTransactionDetail = (invoiceId: string, externalId?: string) => {
    segmentTransactionsTransactionDetail({ externalId });
    history.push(Routes.TRANSACTION_DETAIL.replace(':invoice_id', invoiceId));
  };

  const renderDate = (date?: string) => {
    if (!date) {
      return '-';
    }

    return <Moment format="DD/MM/YY">{createDate(date)}</Moment>;
  };

  const renderCompletedDate = (status: PaymentStatus, creationDate: string) => {
    if (status === PaymentStatus.COMPLETED) {
      return <Moment format="DD/MM/YY">{createDate(creationDate)}</Moment>;
    }

    return <span>---</span>;
  };

  const renderTransactionStatus = (
    trans: Transaction,
    showReviewStatus: boolean,
    displayStatus: string
  ) => {
    if (isTransactionRefunded(trans)) {
      return (
        <>
          <TransactionStatusIcon
            status="COMPLETED"
            tooltipTitle={displayStatus}
          />
          <TransactionStatusIcon
            status="REFUNDED"
            tooltipTitle={l('transactions.status.REFUNDED')}
          />
        </>
      );
    }

    return (
      <TransactionStatusIcon
        status={
          showReviewStatus
            ? 'DECLINED'
            : trans.status ?? 'DECLINED'
        }
        tooltipTitle={displayStatus}
      />
    );
  };

  if (!client || clientsLoading) {
    return (
      <>
        <TopDashboardSection title={l('menu.item.CLIENTS')} subtitle={l('menu.item.CLIENTS.desc')} />
        <div className="client-detail-page__loading">
          <Loading />
        </div>
      </>
    );
  }

  const status = clientSummary?.status ?? getClientStatus(client);
  const statusKey = status.toLowerCase();
  const countryCode = getMappedCountryCode(client.country);
  const currency = client.transactedCurrency || client.currency || '';
  const purchasesCount = clientSummary?.numberOfPurchases ?? allTransactions?.totalElements ?? 0;
  const averageTicket = clientSummary?.averageTicket ?? (
    client.transactedAmount != null && purchasesCount > 0
      ? client.transactedAmount / purchasesCount
      : undefined
  );
  const transactedVolume = clientSummary?.transactedVolume ?? client.transactedAmount;

  const buildTransactionRow = (trans: Transaction) => {
    const eciResult = getEciBrandResult(trans.checkoutPaymentMethodCode, trans.errorLogEciCode);
    const showReviewStatusForTxDetails =
      trans.status === PaymentStatus.CANCELLED && eciResult === ECI_RESULT.SUCCESS;
    const statusText = l(
      trans.status
        ? `transactions.status.${showReviewStatusForTxDetails ? 'DECLINED' : trans.status ?? 'DECLINED'}`
        : 'transactions.status.DECLINED'
    );
    const displayStatus = eciResult === ECI_RESULT.FAILURE ? `${statusText} (3DS)` : statusText;

    const transactionCountry = trans.country ?? trans.checkoutCountry;
    const transactionCountryCode = getMappedCountryCode(transactionCountry);
    const transactionEmail = trans.clientEmail ?? trans.txEmail ?? client?.email ?? '';
    const paymentMethodCode = trans.checkoutPaymentMethodCode;

    const creationDateCell = (
      <span className="client-detail-page__column-left">
        <Moment format="DD/MM/YY">{createDate(trans.creationDate)}</Moment>
      </span>
    );
    const completedDateCell = (
      <span className="client-detail-page__column-left">
        {renderCompletedDate(trans.status, trans.creationDate)}
      </span>
    );
    const copyIdCell = (
      <div className="client-detail-page__copy-cell">
        <CopyToClipboard text={trans.invoiceId} onCopy={() => setCopiedInvoiceId(trans.invoiceId)}>
          <Tooltip
            onMouseEnter={() => setCopiedInvoiceId(null)}
            title={l(copiedInvoiceId === trans.invoiceId ? 'common.copied' : 'common.copy')}
          >
            <button type="button" className="client-detail-page__copy-link">
              <CopyIcon className="client-detail-page__copy-link-icon" aria-hidden />
              <span>{l('clients.copyId')}</span>
            </button>
          </Tooltip>
        </CopyToClipboard>
      </div>
    );
    const paymentMethodCell = (
      <div className="client-detail-page__payment-method-cell">
        {paymentMethodCode ? (
          <img
            alt={trans.paymentMethod ?? trans.checkoutPaymentMethod ?? ''}
            className="client-detail-page__payment-method-icon"
            src={`${Urls.paymentMethodsWide.replace(':code', paymentMethodCode)}`}
          />
        ) : (
          <span>{trans.paymentMethod ?? trans.checkoutPaymentMethod ?? '-'}</span>
        )}
      </div>
    );
    const actionsCell = (
      <div className="client-detail-page__actions-cell">
        {trans.country || showReviewStatusForTxDetails ? (
          <ButtonMore
            variant="detail"
            onClick={() => openTransactionDetail(trans.invoiceId, trans.externalId)}
          />
        ) : (
          <TransactionTooltipButton
            transactionId={trans.invoiceId}
            type={trans.endUserErrorType}
            document={trans.errorLogDocument}
            documentType={trans.errorLogDocumentType}
            email={trans.errorLogEmail}
          />
        )}
      </div>
    );

    return [
      creationDateCell,
      completedDateCell,
      copyIdCell,
      paymentMethodCell,
      <span className="client-detail-page__column-right">
        {trans.currency ?? trans.checkoutLocalCurrency}{' '}
        {trans.localAmount ?? trans.checkoutLocalAmount ?? '-'}
      </span>,
      <span className="client-detail-page__column-right">
        {trans.balanceCurrency ?? ''} {trans.balanceAmount ?? '-'}
      </span>,
      <div className="client-detail-page__status-cell">
        {renderTransactionStatus(trans, showReviewStatusForTxDetails, displayStatus)}
        {!trans.country && !showReviewStatusForTxDetails && (
          <Tooltip
            title={l('transactions.declined.fixTooltip')}
            arrow
            placement="top"
            classes={{
              tooltip: 'client-detail-page__declined-warning-tooltip',
              arrow: 'client-detail-page__declined-warning-tooltip__arrow',
            }}
          >
            <span
              className="client-detail-page__declined-warning-icon-wrap"
              tabIndex={0}
              aria-label={l('transactions.declined.fixTooltip')}
            >
              <DeclinedWarningIcon className="client-detail-page__declined-warning-icon" aria-hidden />
            </span>
          </Tooltip>
        )}
        {trans.chargeback?.externalId && (
          <Tooltip
            title={l('transactions.chargeback.iconTooltip')}
            arrow
            placement="top"
            classes={{
              tooltip: 'client-detail-page__declined-warning-tooltip',
              arrow: 'client-detail-page__declined-warning-tooltip__arrow',
            }}
          >
            <span
              className="client-detail-page__chargeback-icon-wrap"
              tabIndex={0}
              aria-label={l('transactions.chargeback.iconTooltip')}
            >
              <ChargebackIcon className="client-detail-page__chargeback-icon" aria-hidden />
            </span>
          </Tooltip>
        )}
      </div>,
      actionsCell,
    ];
  };

  const transactionHeaders = [
    <span className="client-detail-page__column-left">{l('transactions.createDate')}</span>,
    <span className="client-detail-page__column-left">{l('clients.completedDate')}</span>,
    <span className="client-detail-page__column-left">{l('clients.copyId')}</span>,
    <span className="client-detail-page__column-left">{l('transactions.paymentMethod')}</span>,
    <span className="client-detail-page__column-right">{l('transactions.amountPaid')}</span>,
    <span className="client-detail-page__column-right">{l('transactions.amountReceived')}</span>,
    <span>{l('transactions.status')}</span>,
  ];

  return (
    <>
      <TopDashboardSection title={l('menu.item.CLIENTS')} subtitle={l('menu.item.CLIENTS.desc')} />
      <div className="client-detail-page">
        <Card className="client-detail-page__shell">
          <div className="client-detail-page__toolbar">
            <ButtonLink
              type="primary"
              text={
                <>
                  <BackIcon /> {l('clients.backToClients')}
                </>
              }
              onClick={backToClients}
            />
          </div>
          <div className="client-detail-page__layout">
            <Card nested className="client-detail-page__profile-card">
            <aside className="client-detail-page__profile">
              <div className="client-detail-page__profile-header">
                <span className="client-detail-page__avatar" aria-hidden>
                  {getClientInitial(client)}
                </span>
                <h2 className="client-detail-page__name">{getClientFullName(client)}</h2>
                {client.email && (
                  <div className="client-detail-page__profile-row client-detail-page__profile-row--email">
                    <Mail size={16} strokeWidth={1.75} aria-hidden />
                    <span>{client.email}</span>
                  </div>
                )}
              </div>
              <div className="client-detail-page__profile-divider" aria-hidden />
              <div className="client-detail-page__profile-details">
                <div className="client-detail-page__profile-row">
                  <img
                    alt=""
                    className="client-detail-page__country-flag country-flag--rounded"
                    src={`${Urls.roundedFlags.replace(':code', countryCode)}`}
                  />
                  <span>{countryName(client.country)}</span>
                </div>
                <div className="client-detail-page__profile-date-row">
                  <Calendar size={16} strokeWidth={1.75} aria-hidden />
                  <div className="client-detail-page__profile-date-row__content">
                    <span className="client-detail-page__profile-date-row__label">
                      {l('clients.firstTransaction')}:
                    </span>
                    <span className="client-detail-page__profile-date-row__value">
                      {renderDate(clientSummary?.firstTransactionDate ?? client.firstTransactionDate)}
                    </span>
                  </div>
                </div>
                <div className="client-detail-page__profile-date-row">
                  <Calendar size={16} strokeWidth={1.75} aria-hidden />
                  <div className="client-detail-page__profile-date-row__content">
                    <span className="client-detail-page__profile-date-row__label">
                      {l('clients.lastTransaction')}:
                    </span>
                    <span className="client-detail-page__profile-date-row__value">
                      {renderDate(clientSummary?.lastTransactionDate ?? client.lastTransactionDate)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="client-detail-page__profile-divider" aria-hidden />
              <div className="client-detail-page__status-row">
                <span className="client-detail-page__status-label">{l('clients.status')}:</span>
                <span className={`client-detail-page__status client-detail-page__status--${statusKey}`}>
                  <User size={14} strokeWidth={1.75} aria-hidden />
                  <span>{l(`clients.status.${status}`)}</span>
                </span>
              </div>
            </aside>
            </Card>

            <div className="client-detail-page__content">
              <div className="client-detail-page__stats">
              <Card nested className="client-detail-page__metric-card">
                <div className="client-detail-page__metric">
                  <div className="client-detail-page__metric-main">
                    <div className="client-detail-page__stat-label">{l('clients.transactedVolume')}</div>
                    <div className="client-detail-page__stat-value">
                      {currency} {formatTransactedAmountValue(transactedVolume)}
                    </div>
                  </div>
                  <span className="client-detail-page__stat-icon client-detail-page__stat-icon--volume">
                    <Coins size={28} strokeWidth={1.75} aria-hidden />
                  </span>
                </div>
              </Card>
              <Card nested className="client-detail-page__metric-card">
                <div className="client-detail-page__metric">
                  <div className="client-detail-page__metric-main">
                    <div className="client-detail-page__stat-label">{l('clients.purchasesCount')}</div>
                    <div className="client-detail-page__stat-value">
                      {String(purchasesCount).padStart(2, '0')}
                    </div>
                  </div>
                  <span className="client-detail-page__stat-icon client-detail-page__stat-icon--purchases">
                    <ShoppingBag size={28} strokeWidth={1.75} aria-hidden />
                  </span>
                </div>
              </Card>
              <Card nested className="client-detail-page__metric-card">
                <div className="client-detail-page__metric">
                  <div className="client-detail-page__metric-main">
                    <div className="client-detail-page__stat-label">{l('clients.averageTicket')}</div>
                    <div className="client-detail-page__stat-value">
                      {currency} {formatTransactedAmountValue(averageTicket)}
                    </div>
                  </div>
                  <span className="client-detail-page__stat-icon client-detail-page__stat-icon--ticket">
                    <Receipt size={28} strokeWidth={1.75} aria-hidden />
                  </span>
                </div>
              </Card>
              </div>

              <Card nested className="client-detail-page__table-card">
              <div className="client-detail-page__table-wrapper">
                {transactionsLoading && (
                  <div className="client-detail-page__table-loading">
                    <Loading />
                  </div>
                )}
                {!transactionsLoading && (
                  <Table
                    dense
                    noBorderRadius
                    withBtnMenu
                    withoutEdgeSpacers
                    cellAlign="left"
                    emptyBody={<div className="client-detail-page__table-empty">{l('transactions.noTransaction')}</div>}
                    headers={transactionHeaders}
                    body={allTransactions?.data?.map(buildTransactionRow) ?? null}
                    rowsPerPageOptions={[allTransactions?.size || 10]}
                    rowsLength={allTransactions?.totalElements}
                    rowsPerPage={allTransactions?.size}
                    page={allTransactions?.page}
                    handleChangePage={(event, newPage) => setTransactionsPage(newPage)}
                  />
                )}
              </div>
              </Card>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
};

export default ClientDetailPage;
